import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import type { VideoScreenSpec } from "../scenario/types";
import { registerSceneDuckSink } from "../lib/audio";

const DEG = Math.PI / 180;

/** Shared cache so two screens playing the SAME clip (e.g. two mirrored bar
 *  TVs on one feed) decode it once and stay perfectly in sync, instead of
 *  each mounting its own <video> decoder. `baseVolume` tracks the "intended"
 *  volume as computed by the fade/duck logic below, IGNORING the global
 *  scene duck (`globalTvDuckFactor`) — the actual `video.volume` written to
 *  the element is always `baseVolume * globalTvDuckFactor`, so a persistent
 *  scene-wide duck composes cleanly with the existing open-loud/broadcast
 *  fades instead of fighting them. */
const videoCache = new Map<
  string,
  { video: HTMLVideoElement; texture: THREE.VideoTexture; refs: number; baseVolume: number }
>();

/** Persistent 0..1 multiplier applied on top of every cached TV/video's own
 *  base volume — see `setGlobalTvDuck()`. Lets a narrative beat (e.g. "the
 *  bartender conversation just ended, quiet the room for the rest of the
 *  scene") turn down every background screen at once without touching the
 *  per-clip fade/duck timings above. */
let globalTvDuckFactor = 1;

function setEntryVolume(entry: { video: HTMLVideoElement; baseVolume: number }, base: number, ms: number) {
  entry.baseVolume = base;
  fadeVolume(entry.video, base * globalTvDuckFactor, ms);
}

/** Persistently duck (or restore) every currently-registered background
 *  video screen — called from `audio.ts::duckSceneAudio()` so ambient bed +
 *  TV screens move together. Safe to call before any video exists (the
 *  factor is simply applied to whatever gets created afterward). */
export function setGlobalTvDuck(factor: number, ms = 900) {
  globalTvDuckFactor = Math.max(0, Math.min(1, factor));
  videoCache.forEach((entry) => {
    fadeVolume(entry.video, entry.baseVolume * globalTvDuckFactor, ms);
  });
  if (typeof window !== "undefined") {
    (window as unknown as { __cfTvDuck?: { factor: number; targets: number[] } }).__cfTvDuck = {
      factor: globalTvDuckFactor,
      targets: [...videoCache.values()].map((entry) =>
        Math.round(entry.baseVolume * globalTvDuckFactor * 1000) / 1000,
      ),
    };
  }
}
// Registered once at module load so `audio.duckScene()`/`duckSceneAudio()`
// reaches every background TV screen without `lib/audio.ts` importing this
// react-three component directly (see `registerSceneDuckSink`).
registerSceneDuckSink(setGlobalTvDuck);

/** Gate so an atmospheric background TV never starts making noise while the
 *  learner is still looking at the mission-briefing splash (the Canvas/Scene
 *  — and therefore this video element — mounts as soon as "Start the
 *  simulation" is clicked, well before "Begin mission" dismisses the intro
 *  card). `openTvPlaybackGate()` is called once, from the intro beat's own
 *  CTA click (a real user gesture), which both satisfies browser autoplay
 *  policy AND is the moment the learner actually "begins" — any video
 *  created before the gate opens queues its own play() call instead of
 *  firing immediately. */
let playbackGateOpen = false;
const pendingPlays: Array<() => void> = [];
export function openTvPlaybackGate() {
  if (playbackGateOpen) return;
  playbackGateOpen = true;
  const q = pendingPlays.splice(0);
  q.forEach((fn) => fn());
}

/** Generic volume ramp (shared by the initial "open loud" fade and the
 *  post-broadcast duck below) — a plain rAF tween so both can co-exist
 *  without stomping each other as long as they're never running at once. */
function fadeVolume(video: HTMLVideoElement, to: number, ms: number) {
  const marked = video as HTMLVideoElement & { __cfFadeToken?: number };
  const token = (marked.__cfFadeToken ?? 0) + 1;
  marked.__cfFadeToken = token;
  const from = video.volume;
  if (Math.abs(from - to) < 0.005 || ms <= 0) {
    video.volume = to;
    return;
  }
  const start = performance.now();
  const step = (now: number) => {
    if (marked.__cfFadeToken !== token) return;
    const p = Math.min(1, (now - start) / ms);
    const eased = 1 - (1 - p) * (1 - p); // ease-out
    video.volume = from + (to - from) * eased;
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/** When the TV audio actually starts, open a bit louder than the resting
 *  background level (so a learner walking up notices it), then ease down
 *  over a few seconds to the intended ambient level. Runs once per video
 *  element regardless of how many screens/refs share it. */
function attachVolumeFade(
  video: HTMLVideoElement,
  target: number,
  entry: { video: HTMLVideoElement; baseVolume: number },
  openBoost = 0.32,
) {
  if ((video as HTMLVideoElement & { __cfFadeDone?: boolean }).__cfFadeDone) return;
  const marked = video as HTMLVideoElement & { __cfFadeDone?: boolean };
  marked.__cfFadeDone = true;
  // A screen that must stay under nearby dialogue sets openBoost to 0.
  // The default 0.32 is the old "notice the TV, then settle" rise.
  if (openBoost <= 0) return;
  const openLoud = Math.min(0.9, target + openBoost);
  const onPlaying = () => {
    video.removeEventListener("playing", onPlaying);
    entry.baseVolume = openLoud;
    video.volume = openLoud * globalTvDuckFactor;
    setEntryVolume(entry, target, 7000);
  };
  video.addEventListener("playing", onPlaying);
}

/** Duck the volume further once the loop rolls past the scripted broadcast
 *  segment (the anchor clip ends on "...stay with us", then it's just a
 *  looping music bed) — background music should sit well under any nearby
 *  dialogue, unlike the attention-grabbing news open. Re-applies every loop
 *  (currentTime wrapping back under the threshold resets the flag), and
 *  never fights the initial 7s open-loud fade above (that finishes long
 *  before this threshold on any realistic anchor-segment length). */
function attachBroadcastDuck(
  video: HTMLVideoElement,
  target: number,
  duckAfterS: number,
  entry: { video: HTMLVideoElement; baseVolume: number },
) {
  if ((video as HTMLVideoElement & { __cfDuckDone?: boolean }).__cfDuckDone) return;
  const marked = video as HTMLVideoElement & { __cfDuckDone?: boolean };
  marked.__cfDuckDone = true;
  const duckTarget = Math.max(0.04, target * 0.4);
  let inDuck = false;
  video.addEventListener("timeupdate", () => {
    const t = video.currentTime;
    if (t >= duckAfterS && !inDuck) {
      inDuck = true;
      setEntryVolume(entry, duckTarget, 1800);
    } else if (t < duckAfterS && inDuck) {
      inDuck = false;
      setEntryVolume(entry, target, 600);
    }
  });
}

function acquireVideo(
  url: string,
  volume: number,
  duckAfterS?: number,
  openBoost = 0.32,
): { video: HTMLVideoElement; texture: THREE.VideoTexture } {
  const existing = videoCache.get(url);
  if (existing) {
    existing.refs += 1;
    return existing;
  }
  const video = document.createElement("video");
  video.src = url;
  video.crossOrigin = "anonymous";
  video.loop = true;
  video.muted = false;
  video.volume = volume * globalTvDuckFactor;
  video.playsInline = true;
  video.preload = "auto";
  const entry = { video, texture: null as unknown as THREE.VideoTexture, refs: 1, baseVolume: volume };
  attachVolumeFade(video, volume, entry, openBoost);
  if (duckAfterS && duckAfterS > 0) attachBroadcastDuck(video, volume, duckAfterS, entry);
  const tryPlay = () => {
    const attempt = video.play();
    if (!attempt) return;
    attempt.catch(() => {
      // A deep link never clicks Begin mission, so a clip with sound
      // stays on frame 0. Muted playback is allowed without a gesture;
      // the next click unmutes it.
      if (video.muted) return;
      video.muted = true;
      video.play().catch(() => {
        /* still blocked */
      });
    });
  };
  if (playbackGateOpen) tryPlay();
  else pendingPlays.push(tryPlay);
  video.addEventListener("loadeddata", () => {
    if (playbackGateOpen && video.paused) tryPlay();
  });
  if (typeof window !== "undefined") {
    const retry = () => {
      if (!playbackGateOpen) return;
      if (video.muted) video.muted = false;
      if (video.paused) tryPlay();
      if (!video.paused && !video.muted) {
        window.removeEventListener("pointerdown", retry);
        window.removeEventListener("keydown", retry);
      }
    };
    window.addEventListener("pointerdown", retry);
    window.addEventListener("keydown", retry);
  }
  const texture = new THREE.VideoTexture(video);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  entry.texture = texture;
  videoCache.set(url, entry);
  return { video, texture };
}

function releaseVideo(url: string) {
  const entry = videoCache.get(url);
  if (!entry) return;
  entry.refs -= 1;
  if (entry.refs <= 0) {
    entry.video.pause();
    entry.video.src = "";
    entry.texture.dispose();
    videoCache.delete(url);
  }
}

/** Hard-stop + evict EVERY currently cached background video regardless of
 *  ref count — a belt-and-suspenders reset for a hard cut into a room that
 *  has no `videoScreens` of its own (e.g. bar -> office). Normal unmount
 *  already releases refs correctly, but this guarantees zero audible
 *  audio survives the cut even if a ref-counted release is ever delayed by
 *  a re-render race, instead of relying on timing. Safe to call any time,
 *  including with an empty cache. */
export function stopAllVideoScreens() {
  videoCache.forEach((entry) => {
    try {
      entry.video.pause();
      entry.video.src = "";
      entry.texture.dispose();
    } catch {
      /* ignore */
    }
  });
  videoCache.clear();
}

/**
 * A wall-mounted screen ALWAYS playing a looping MP4 (e.g. a TV tuned to a
 * sports channel in the background of a bar) — purely atmospheric, no on/off
 * hotspot. A subtle randomized flicker on the emissive intensity sells the
 * "cheap TV / CRT-ish panel" feel instead of a perfectly flat backlight.
 */
export function VideoScreen({ spec }: { spec: VideoScreenSpec }) {
  const matRef = useRef<THREE.MeshStandardMaterial>(null);
  const flickerPhase = useRef(Math.random() * 100);
  const volume = spec.volume ?? 0.35;
  const openBoost = spec.openBoost ?? 0.32;
  const flicker = spec.flicker ?? true;
  const yaw = (spec.rotationDeg ?? 0) * DEG;

  const { texture } = useMemo(
    () => acquireVideo(spec.videoUrl, volume, spec.duckAfterS, openBoost),
    [spec.videoUrl, volume, spec.duckAfterS, openBoost],
  );
  useEffect(() => () => releaseVideo(spec.videoUrl), [spec.videoUrl]);

  useFrame((_, delta) => {
    const mat = matRef.current;
    if (!mat) return;
    if (!flicker) {
      mat.emissiveIntensity = 1.0;
      return;
    }
    flickerPhase.current += delta;
    const t = flickerPhase.current;
    // Slow "breathing" brightness + fast fine grain + rare short dips, so it
    // reads as an aging TV panel rather than a static emissive plane.
    const breathe = Math.sin(t * 1.7) * 0.03;
    const grain = (Math.sin(t * 41.0) * 0.5 + Math.random() * 0.5 - 0.25) * 0.025;
    const dip = Math.random() < 0.004 ? -0.35 : 0;
    mat.emissiveIntensity = Math.max(0.55, 1.0 + breathe + grain + dip);
  });

  return (
    <group position={spec.screenCenter} rotation={[0, yaw, 0]}>
      <mesh position={[0, 0, 0.015]}>
        <planeGeometry args={[spec.width, spec.height]} />
        <meshStandardMaterial
          ref={matRef}
          map={texture}
          emissive="#ffffff"
          emissiveMap={texture}
          emissiveIntensity={1.0}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}
